import React, { FC } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { signIn } from 'next-auth/react';
import { useTheme } from '@emotion/react';
import { Text } from '../text';
import { mq } from '$lib/helpers';
import { Flex } from '$components/layouts';
import { useTranslation } from '$hooks';
import { Button } from '../button';
import { SidePanel } from '../side-panel';
import { useAuthentication } from '~/store';
import { INavigation } from '$models';

export type MemberAccessPanelProps = {
    navigation: INavigation;
};

const ShowBusinessAccountButton = true;

export const MemberAccessPanel: FC<MemberAccessPanelProps> = ({ navigation }) => {
    const { setShowMemberAccessPanel, showMemberAccessPanel, authReturnUrl } = useAuthentication();
    const { translate } = useTranslation();
    const theme = useTheme();
    const { asPath, query } = useRouter();
    const { enableBuisnessCreation } = query;

    return (
        <SidePanel show={!!showMemberAccessPanel} onChange={() => setShowMemberAccessPanel(undefined)}>
            <SidePanelContainer alignItems="center" column flex>
                <ContentContainer column flex>
                    <Title h2>{translate(`loginPage.panel.loginTitle`)}</Title>
                    {!!translate(`loginPage.panel.loginIntroText`) && (
                        <InfoText bottomMargin="8">{translate(`loginPage.panel.loginIntroText`)}</InfoText>
                    )}

                    <Button
                        variant="primary"
                        width="expansive"
                        onClick={() =>
                            signIn('azure-ad-b2c', {
                                callbackUrl:
                                    asPath === '/' && navigation?.myPageHome?.path
                                        ? navigation.myPageHome.path
                                        : authReturnUrl || asPath || navigation?.myPageHome?.path,
                            })
                        }
                    >
                        {translate('loginPage.panel.loginWithEmail')}
                    </Button>

                    <Divider justifyContent="center" style={{ textTransform: 'uppercase' }}>
                        <DividerText color={theme.colors.grey40}>
                            {translate('loginPage.panel.dividerText')}
                        </DividerText>
                    </Divider>

                    <Button
                        variant="inverted"
                        width="expansive"
                        style={{ marginBottom: 20 }}
                        onClick={() =>
                            signIn('azure-ad-b2c-signup', {
                                callbackUrl:
                                    asPath === '/' && navigation?.myPageHome?.path
                                        ? navigation.myPageHome.path
                                        : authReturnUrl || asPath || navigation?.myPageHome?.path,
                            })
                        }
                    >
                        {translate('loginPage.panel.signupWithEmail')}
                    </Button>

                    <InfoText style={{ marginTop: 20 }}>{translate(`loginPage.panel.loginOutroText`)}</InfoText>

                    {ShowBusinessAccountButton || !!enableBuisnessCreation ? (
                        <>
                            <Divider justifyContent="center" style={{ textTransform: 'uppercase' }}>
                                <DividerText color={theme.colors.grey40}>
                                    {translate(`loginPage.panel.businessAccountSection`)}
                                </DividerText>
                            </Divider>

                            <Button
                                variant="primary"
                                width="expansive"
                                onClick={() =>
                                    signIn('azure-ad-b2c-signup-business', {
                                        callbackUrl:
                                            asPath === '/' && navigation?.myPageHome?.path
                                                ? navigation.myPageHome.path
                                                : authReturnUrl || asPath || navigation?.myPageHome?.path,
                                    })
                                }
                            >
                                {translate('loginPage.panel.createBusinessAccount')}
                            </Button>
                        </>
                    ) : null}
                </ContentContainer>
            </SidePanelContainer>
        </SidePanel>
    );
};

const SidePanelContainer = styled(Flex)(({ theme }) => ({
    padding: `0 ${theme.space[6]} 0 ${theme.space[6]}`,

    [mq('sm')]: {
        padding: `0 ${theme.space[9]} 60px ${theme.space[9]}`,
    },
}));

const ContentContainer = styled(Flex)({
    width: '100%',
    maxWidth: 400,
    paddingBottom: 100,
});

const Divider = styled(Flex)(({ theme }) => ({
    position: 'relative',
    marginTop: theme.space[9],
    marginBottom: theme.space[9],

    '&:after': {
        content: '""',
        width: '100%',
        height: 1,
        backgroundColor: theme.colors.grey15,
        position: 'absolute',
        zIndex: -1,
        top: '50%',
    },
}));

const DividerText = styled(Text)(({ theme }) => ({
    backgroundColor: theme.colors.white,
    padding: '0 10px',
}));

const Title = styled(Text)({
    marginBottom: 32,
});

const InfoText = styled(Text)({});
